import { Spacing } from '@persona-id/design-tokens';
import { cloneElement, ComponentPropsWithoutRef, ElementType, ReactElement } from 'react';
import styled, { css } from 'styled-components';

import Badge from 'design-system/components/Badge';
import Button from 'design-system/components/Button';
import { isActive, isFocused, isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import { paragraphMixins } from 'design-system/mixins/text';
import { TransitionDuration } from 'design-system/variables/transitions';

interface TabProps<T extends ElementType = 'button'>
  extends ComponentPropsWithoutRef<typeof Button> {
  selected?: boolean;
  badge?: ReactElement;
  as?: T;
}

const BadgeContainer = styled.span`
  padding: 0px;
  line-height: 0;
  margin-left: ${Spacing[1]};
`;

const tabSelectedStyle = css`
  color: ${({ theme }) => theme.color.primary.brand};
  border-bottom-color: ${({ theme }) => theme.color.primary.brand};
  box-shadow: none;
  &:after {
    transition: border-bottom-color ${TransitionDuration.Fast};
    border-bottom-color: ${({ theme }) => theme.color.primary.brand};
  }
`;

export const StyledTab = styled(Button).attrs<TabProps>({
  width: 'auto',
  variant: 'basic',
})<TabProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  white-space: nowrap;
  background-color: transparent;
  color: ${({ theme, disabled }) =>
    disabled ? theme.color.variants.basic.textDisabled : theme.color.text.tertiary};
  height: 32px;

  margin: 0px;
  padding: 0px;
  ${paragraphMixins.md};
  outline: none;
  border-radius: ${({ theme }) => theme.button.borderRadius};

  a {
    color: currentColor;
  }

  /* remove default button focused/hovered box shadow */
  &${isActive}${isNotDisabled} {
    background-color: unset;
  }
  &${isHovered}${isNotDisabled} {
    box-shadow: none;
  }
  &${isFocused}${isNotDisabled} {
    box-shadow: none;
  }
  &${isHovered}${isFocused}${isNotDisabled} {
    box-shadow: none;
  }
  &:disabled {
    background-color: unset;
  }
  &:focus-visible${isNotDisabled}, &${isHovered}${isNotDisabled}:focus-visible {
    box-shadow: 0 0 0 3px ${({ theme }) => theme.color.variants.basic.highlight};
    &:after {
      border-bottom-left-radius: ${({ theme }) => theme.button.borderRadius};
      border-bottom-right-radius: ${({ theme }) => theme.button.borderRadius};
      transition:
        border-bottom-left-radius ${TransitionDuration.Fast},
        border-bottom-right-radius ${TransitionDuration.Fast};
    }
  }

  &:after {
    position: absolute;
    bottom: 0px;
    width: 100%;
    border-bottom: 2px solid transparent;
    content: '';
  }

  &.selected,
  &.active {
    ${tabSelectedStyle}
  }

  ${({ selected }) => selected && tabSelectedStyle}

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
    `}

  a& {
    text-decoration: none;
  }
`;

const Tab = <T extends ElementType = 'button'>({
  selected,
  disabled,
  children,
  badge,
  as,
  ...elemProps
}: TabProps<T> & ComponentPropsWithoutRef<T>) => {
  return (
    <StyledTab
      role='tab'
      selected={selected}
      aria-selected={selected}
      aria-disabled={disabled}
      disabled={disabled}
      as={as as any}
      {...elemProps}
    >
      {children}
      {badge && (
        <BadgeContainer>
          {badge.type === Badge
            ? cloneElement(badge, { variant: selected ? 'active' : 'basic' })
            : badge}
        </BadgeContainer>
      )}
    </StyledTab>
  );
};

export default Tab;
